"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import {
  fetchTokens,
  createToken,
  updateToken,
  deleteToken,
  regenerateToken,
} from "@/store/modules/tokens/thunks";
import {
  clearTokens,
  setSelectedEstablishment,
} from "@/store/modules/tokens/slice";
import { CreateTokenData, UpdateTokenData } from "@/store/modules/tokens/types";
import { toast } from "react-toastify";

const useTokens = () => {
  const { tokens, pagination, loading, error, selectedEstablishmentId } =
    useSelector((state: any) => state.tokens);
  const dispatch: AppDispatch = useDispatch();

  // Estados locais
  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    per_page: 15,
  });

  // Carregar tokens de um estabelecimento
  const handleFetchTokens = useCallback(
    async (establishmentId: number, params?: any) => {
      try {
        dispatch(setSelectedEstablishment(establishmentId));
        await dispatch(fetchTokens({ establishmentId, params })).unwrap();
      } catch (err: any) {
        if (err?.error) {
          toast.error(err.error);
        } else {
          toast.error("Erro ao buscar tokens.");
        }
        dispatch(clearTokens());
      }
    },
    [dispatch],
  );

  // Criar novo token
  const handleCreateToken = useCallback(
    async (establishmentId: number, data: CreateTokenData) => {
      try {
        const resultAction = await dispatch(
          createToken({ establishmentId, data }),
        );

        if (createToken.fulfilled.match(resultAction)) {
          toast.success("Token criado com sucesso!");
          return { success: true, data: resultAction.payload.data };
        } else {
          toast.error("Erro ao criar token.");
          return { success: false };
        }
      } catch (error) {
        toast.error("Erro ao criar token.");
        console.error("Erro ao criar token:", error);
        return { success: false };
      }
    },
    [dispatch],
  );

  // Atualizar token
  const handleUpdateToken = useCallback(
    async (establishmentId: number, tokenId: number, data: UpdateTokenData) => {
      try {
        const resultAction = await dispatch(
          updateToken({ establishmentId, tokenId, data }),
        );

        if (updateToken.fulfilled.match(resultAction)) {
          toast.success("Token atualizado com sucesso!");
          return { success: true, data: resultAction.payload.data };
        } else {
          toast.error("Erro ao atualizar token.");
          return { success: false };
        }
      } catch (error) {
        toast.error("Erro ao atualizar token.");
        console.error("Erro ao atualizar token:", error);
        return { success: false };
      }
    },
    [dispatch],
  );

  // Excluir token
  const handleDeleteToken = useCallback(
    async (establishmentId: number, tokenId: number) => {
      try {
        const resultAction = await dispatch(
          deleteToken({ establishmentId, tokenId }),
        );

        if (deleteToken.fulfilled.match(resultAction)) {
          toast.success("Token excluído com sucesso!");
          return { success: true };
        } else {
          toast.error("Erro ao excluir token.");
          return { success: false };
        }
      } catch (error) {
        toast.error("Erro ao excluir token.");
        console.error("Erro ao excluir token:", error);
        return { success: false };
      }
    },
    [dispatch],
  );

  // Regenerar token
  const handleRegenerateToken = useCallback(
    async (establishmentId: number, tokenId: number) => {
      try {
        const resultAction = await dispatch(
          regenerateToken({ establishmentId, tokenId }),
        );

        if (regenerateToken.fulfilled.match(resultAction)) {
          toast.success("Token regenerado com sucesso!");
          return { success: true, data: resultAction.payload.data };
        } else {
          toast.error("Erro ao regenerar token.");
          return { success: false };
        }
      } catch (error) {
        toast.error("Erro ao regenerar token.");
        console.error("Erro ao regenerar token:", error);
        return { success: false };
      }
    },
    [dispatch],
  );

  // Limpar tokens
  const handleClearTokens = useCallback(() => {
    dispatch(clearTokens());
  }, [dispatch]);

  // Definir token como padrão (via update)
  const handleSetDefaultToken = useCallback(
    async (establishmentId: number, tokenId: number) => {
      return await handleUpdateToken(establishmentId, tokenId, {
        is_default: true,
      });
    },
    [handleUpdateToken],
  );

  // Filtrar tokens
  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      page: 1,
      per_page: 15,
    });
  };

  // Refrescar tokens do estabelecimento atual
  const handleRefresh = useCallback(async () => {
    if (selectedEstablishmentId) {
      await handleFetchTokens(selectedEstablishmentId, filters);
    }
  }, [selectedEstablishmentId, filters, handleFetchTokens]);

  return {
    tokens,
    pagination,
    loading,
    error,
    selectedEstablishmentId,
    filters,
    handleFetchTokens,
    handleCreateToken,
    handleUpdateToken,
    handleDeleteToken,
    handleRegenerateToken,
    handleSetDefaultToken,
    handleClearTokens,
    handleFilterChange,
    handleClearFilters,
    handleRefresh,
  };
};

export default useTokens;
